import type { ZrmClickstreamListing, ZrmClickstreamUser, ZrmListingInfo, ZrmUserInfo } from '@zg-rentals/clickstream';

const zrmUserInfoNullValues = {
  zrm_user_info: {
    multi_family_customer_ind: null,
    single_family_enterprise_customer_ind: null,
    active_listing_cnt: null,
    total_listing_cnt: null,
  },
};

export function createZrmUserInfo(user?: ZrmClickstreamUser | null): ZrmUserInfo {
  try {
    if (!user) {
      return zrmUserInfoNullValues;
    }
    const { summary = {}, isMultifamilyCustomer = false, isSingleFamilyEnterpriseCustomer = false } = user;
    return {
      zrm_user_info: {
        multi_family_customer_ind: Boolean(isMultifamilyCustomer),
        single_family_enterprise_customer_ind: Boolean(isSingleFamilyEnterpriseCustomer),
        ...(typeof summary.activeListingCount !== 'undefined' && {
          active_listing_cnt: Number(summary.activeListingCount),
        }),
        ...(typeof summary.totalListingCount !== 'undefined' && {
          total_listing_cnt: Number(summary.totalListingCount),
        }),
      },
    };
  } catch (error) {
    return zrmUserInfoNullValues;
  }
}

const PREMIUM_PRODUCT_TYPE_NAME_ID = 6;

const EVENT_PRODUCT_TYPE_OPTIONS = {
  FREE: 'free',
  PREMIUM: 'premium',
  SINGLE_FAMILY_ENTERPRISE: 'singleFamilyEnterprise',
  MULTIFAMILY_ENTERPRISE: 'multifamilyEnterprise',
} as const;

const zrmListingInfoNullValues = {
  zrm_listing_info: {
    applications_ind: null,
    listing_id: null,
    rental_protection_ind: null,
    product_type_txt: null,
    previously_activated_ind: null,
    property_type_cd: null,
    manual_ind: null,
  },
};

export function createZrmListingInfo(listing?: ZrmClickstreamListing): ZrmListingInfo {
  try {
    if (!listing) {
      return zrmListingInfoNullValues;
    }

    const {
      listingAlias,
      // in new web analytics endpoint
      isRentGuaranteeEnabled,
      // in legacy webAnalytics endpoint
      rentGuarantee,
      // in both web analytics endpoints
      eventProductType = '',
      propertyTypeCategory = 'SingleFamily',
      isApplicationsIndicator = false,
      isManual = false,
      hasNeverBeenActivated = false,
      // only included in currentProperty
      freemium,
      rentalProtection,
      webAnalytics,
      isApplicationsInviteOnly = false,
      // part of legacy webAnalytics endpoint and currentProperty
      isApplicationsAccepted = false,
    } = listing;

    if (!webAnalytics) {
      return {
        zrm_listing_info: {
          listing_id: String(listingAlias),
          previously_activated_ind: hasNeverBeenActivated === false,
          property_type_cd: propertyTypeCategory,
          product_type_txt: eventProductType,
          manual_ind: isManual,
          applications_ind: isApplicationsIndicator,
          rental_protection_ind:
            Boolean(isRentGuaranteeEnabled) || Boolean(rentGuarantee?.isEligible && rentGuarantee?.isEnabled),
        },
      };
    }

    // if arg is `currentProperty` as `currentProperty`
    const productTypeTxt =
      webAnalytics.eventProductType === EVENT_PRODUCT_TYPE_OPTIONS.FREE &&
      freemium?.isEnabled &&
      freemium?.activeProduct?.type === PREMIUM_PRODUCT_TYPE_NAME_ID
        ? EVENT_PRODUCT_TYPE_OPTIONS.PREMIUM
        : webAnalytics.eventProductType;

    return {
      zrm_listing_info: {
        applications_ind: isApplicationsInviteOnly || isApplicationsAccepted,
        listing_id: String(listingAlias),
        rental_protection_ind: Boolean(
          (rentGuarantee?.isEligible || rentalProtection?.isEligible) &&
            (rentGuarantee?.isEnabled || rentalProtection?.isEnabled),
        ),
        product_type_txt: productTypeTxt || '',
        previously_activated_ind: webAnalytics.hasNeverBeenActivated === false,
        property_type_cd: propertyTypeCategory,
        manual_ind: Boolean(webAnalytics.isManual),
      },
    };
  } catch (error) {
    return zrmListingInfoNullValues;
  }
}
